<template>
  <OpenSeadragon
    v-if="elements"
    key="viewer"
    @open="handleOpen"
    ref="openseadragon"
    :options="options"
    :style="styles"
  >
    <div class="toolbar-wrapper">
      <slot name="toolbar" v-bind="{ handleAction }"></slot>
    </div>
    <b-loading :active="!isReady" :is-full-page="false" style="z-index: 2"></b-loading>
  </OpenSeadragon>
</template>

<script>
 import EventBus from '@/utils/event-bus.js'

 import { createNamespacedHelpers } from 'vuex'

 const { mapState, mapActions, mapGetters } = createNamespacedHelpers('browser')

 import OpenSeadragon from '@/components/OpenSeadragon.vue'

 import createOverlay from '@/utils/overlay.js'

 export default {
   components: {
     OpenSeadragon
   },
   props: {
     source: {
       type: [String, Array, Object],
       required: true
     },
     elements: {
       type: Array,
       default: () => []
     }
   },
   watch: {
     elements(elements) {
       if (this.viewerOverlay) {
	 this.viewerOverlay.update(elements)
       }
     }
   },
   data() {
     return { isReady: false }
   },
   computed: {
     canLoadImage() {
       return this.source !== null
     },
     canRenderOverlays() {
       return this.overlayElements !== null
     },
     viewer() {
       return this.$refs.openseadragon.viewer
     },
     options() {
       return {
	 tileSources: this.source,
         showZoomControl: false,
         showHomeControl: false,
         showFullPageControl: false,
	 preserveImageSizeOnResize: true,
	 gestureSettingsMouse: {
	   clickToZoom: false
	 },
	 visibilityRatio: 1
       }
     },
     styles() {
       return {
	 width: '100%',
	 height: '100%',
	 position: 'relative'
       }
     }
   },
   created() {
     EventBus.$on('INCREASE_ZOOM', this.handleIncreaseZoom)
     EventBus.$on('DECREASE_ZOOM', this.handleDecreaseZoom)
   },
   beforeDestroy() {
     EventBus.$off('INCREASE_ZOOM', this.handleIncreaseZoom)
     EventBus.$off('DECREASE_ZOOM', this.handleDecreaseZoom)
   },
   methods: {
     handleOpen() {
       this.setupViewer()

       if (this.canRenderOverlays) {
	 this.viewerOverlay = createOverlay({
	   openSeadragon: this.viewer,
	   elements: this.elements,
	   onOverlayClick: (...args) => this.handleOverlayClick(...args)
	 })

	 this.$nextTick(() => this.viewerOverlay.update(this.elements))
       }

       // leave some time for openseadragon to render tiles
       setTimeout(() => this.isReady = true, 250)
     },
     handleOverlayClick(element) {
       console.log(element)
       this.$emit('click', element.id)
     },
     handleAction(data) {},
     handleIncreaseZoom() {
       this.viewer.viewport.zoomBy(this.viewer.zoomPerClick)
       this.viewer.viewport.applyConstraints()
     },
     handleDecreaseZoom() {
       this.viewer.viewport.zoomBy(1 / this.viewer.zoomPerClick)
       this.viewer.viewport.applyConstraints()
     },
     setupViewer() {
       const viewportBounds = this.viewer.viewport.getBounds()
       const viewportAspect = viewportBounds.height / viewportBounds.width
       const newBounds = this.viewer.world.getHomeBounds()

       newBounds.height = newBounds.width * viewportAspect

       this.viewer.viewport.fitBounds(newBounds, true)
     }
   }
 }
</script>

<style lang="scss">
 @import "@/assets/colors.scss";

 .toolbar-wrapper {
   position: absolute;
   left: 0;
   top: 0;
   z-index: 1;

   padding: 1rem;
 }

 .overlay-backdrop {
   background: transparent;
 }

 .overlay-backdrop.is-hidden {
   background: transparent;
 }

 .overlay-element {
   overflow: hidden;
   cursor: pointer;

   text-overflow: ellipsis;

   background-color: rgba(255, 255, 255, 0.125);
   border: 2px solid rgba(0, 0, 0, 0.125);

   box-sizing: border-box;
 }

 .overlay-element.is-match {
   border-color: rgba($dark-red, 0.3);
   background-color: rgba($dark-red, 0.05);
 }

 .overlay-element.is-highlighted {
   border-color: rgba($secondary, 0.8);
   background-color: rgba($secondary, 0.1);
 }

 .overlay-element.is-text-visible.is-highlighted {
   border-color: rgba($primary, 0.75);
   background-color: rgba(255, 255, 255, 0.75);
 }

 .overlay-element.is-text-visible {
   display: block !important;
   background-color: rgba(255, 255, 255, 0.75);
   border: 2px solid rgba(0, 0, 0, 0.25);
 }

 .overlay-element .overlay-label {
   display: none;
 }
 
 .overlay-element.is-text-visible .overlay-label {
   display: flex;
 }

 .overlay-label {
   display: flex;
   flex-flow: row nowrap;
   justify-content: center;
   align-items: center;
   white-space: pre;
   text-overflow: ellipsis;
   height: 100%;

   will-change: transform;
 }
</style>
